import XLogo from '@clearkit/icons/identity/XDark';
import AudiencesNavIcon from '@clearkit/icons/nav/Audiences';
import CompaniesNavIcon from '@clearkit/icons/nav/Companies';
import FormsNavIcon from '@clearkit/icons/nav/Forms';
import HomeNavIcon from '@clearkit/icons/nav/Home';
import PeopleNavIcon from '@clearkit/icons/nav/People';
import SandboxNavIcon from '@clearkit/icons/nav/Sandbox';
import SettingsNavIcon from '@clearkit/icons/nav/Settings';
import SetupNavIcon from '@clearkit/icons/nav/Setup';
import SwitcherNavIcon from '@clearkit/icons/nav/Switcher';
import List from '@material-ui/core/List';
import Popover from '@material-ui/core/Popover';
import RootRef from '@material-ui/core/RootRef';
import { withRouter } from 'react-router';
import { NavLink } from 'react-router-dom';
import { Subscribe } from 'unstated';

import AppSwitch from '~/components/AppSwitch';
import CompanyLogo from '~/components/CompanyLogo';
import UserContainer from '~/containers/User';
import Caret from '~/images/Navigation/Caret.svg';
import paths from '~/lib/paths';

import styles from './Sidebar.style';
import UserMenu from './sidebar/UserMenu/UserMenu';
import SidebarNavLink from './SidebarNavLink';

const Sidebar = ({
  classes,
  userContainer,
  history: {
    location: { pathname },
  },
}) => {
  const [configOpen, setConfigOpen] = useState(false);
  const [appSwitchOpen, setAppSwitchOpen] = useState(false);
  const configMenuButton = useRef();
  const appSwitchButton = useRef();

  const renderMenuContents = () => {
    if (userContainer.hasNoAccess()) {
      return <List className={classes.list} />;
    }

    if (pathname === '/welcome' || userContainer.needsToOnboard()) {
      return (
        <List className={classes.list}>
          <SidebarNavLink
            className={classes.logoLink}
            classes={classes}
            to="/welcome"
            title="Welcome"
            icon={<SetupNavIcon />}
          />

          {userContainer.shouldShowForms() && (
            <SidebarNavLink
              classes={classes}
              to="/forms"
              title="Forms"
              icon={<FormsNavIcon />}
            />
          )}
        </List>
      );
    }

    return (
      <List className={classes.list}>
        {userContainer.shouldShowDashboard() && (
          <SidebarNavLink
            exact
            className={classes.logoLink}
            classes={classes}
            to="/"
            title="Home"
            icon={<HomeNavIcon />}
          />
        )}

        {userContainer.shouldShowPeople() && (
          <SidebarNavLink
            classes={classes}
            to="/people"
            title="People"
            icon={<PeopleNavIcon />}
          />
        )}

        {userContainer.shouldShowCompanies() && (
          <SidebarNavLink
            classes={classes}
            to="/companies"
            title="Companies"
            icon={<CompaniesNavIcon />}
          />
        )}
        {userContainer.shouldShowForms() && (
          <SidebarNavLink
            classes={classes}
            to="/forms"
            title="Forms"
            icon={<FormsNavIcon />}
          />
        )}

        <SidebarNavLink
          classes={classes}
          to={paths.audiences()}
          title="Audiences"
          icon={<AudiencesNavIcon />}
        />

        {userContainer.shouldShowSettings() && (
          <SidebarNavLink
            classes={classes}
            to="/settings/configuration"
            title="Configuration"
            icon={<SettingsNavIcon />}
          />
        )}
      </List>
    );
  };

  const domain = userContainer.accountDomain();

  return (
    <nav className={classes.root}>
      <NavLink className={classes.xLogo} to="/">
        <XLogo width="36" height="36" />
      </NavLink>

      {renderMenuContents()}

      <div className={classes.spacer} />

      <List className={classes.lowerList}>
        {process.env.NODE_ENV === 'development' && (
          <SidebarNavLink
            classes={classes}
            to="/sandbox"
            title="Sandbox"
            icon={<SandboxNavIcon />}
          />
        )}

        <RootRef rootRef={appSwitchButton}>
          <SidebarNavLink
            className={classnames(classes.listItem, classes.lowerListItem)}
            classes={classes}
            href="#none"
            title="Switch applications"
            onClick={(e) => {
              e.preventDefault();
              setAppSwitchOpen(true);
            }}
            icon={<SwitcherNavIcon />}
          />
        </RootRef>

        <RootRef rootRef={configMenuButton}>
          <SidebarNavLink
            className={classnames(
              classes.listItem,
              classes.lowerListItem,
              classes.companyListItem,
            )}
            classes={classes}
            href="#none"
            title="Switch Team"
            onClick={(e) => {
              e.preventDefault();
              setConfigOpen(true);
            }}
            icon={
              <>
                <CompanyLogo
                  domain={domain}
                  className={classes.companyIcon}
                  size={28}
                />
                <Caret className={classes.caret} />
              </>
            }
          />
        </RootRef>

        <Popover
          classes={{ paper: classes.paper }}
          open={appSwitchOpen}
          anchorEl={appSwitchButton.current}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClose={() => setAppSwitchOpen(false)}>
          <AppSwitch />
        </Popover>

        <UserMenu
          popoverProps={{
            classes: { paper: classes.paper },
            open: configOpen,
            anchorEl: configMenuButton.current,
            anchorOrigin: { vertical: 'top', horizontal: 'left' },
            transformOrigin: { vertical: 'bottom', horizontal: 'left' },
            onClose: () => setConfigOpen(false),
          }}
        />
      </List>
    </nav>
  );
};

const WrappedSidebar = (props) => (
  <Subscribe to={[UserContainer]}>
    {(userContainer) => <Sidebar {...props} userContainer={userContainer} />}
  </Subscribe>
);

Sidebar.propTypes = {
  beta: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  history: PropTypes.object,
  view: PropTypes.string,
  userContainer: PropTypes.shape({
    accountDomain: PropTypes.func.isRequired,
    shouldShowCompanies: PropTypes.func.isRequired,
    shouldShowDashboard: PropTypes.func.isRequired,
    shouldShowSettings: PropTypes.func.isRequired,
    shouldShowPeople: PropTypes.func.isRequired,
    shouldShowForms: PropTypes.func.isRequired,
    hasNoAccess: PropTypes.func.isRequired,
    needsToOnboard: PropTypes.func.isRequired,
  }),
};

export default withRouter(withStyles(styles)(WrappedSidebar));
